import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { HUNT_DIY_ACTIVITY } from '../../marketplace-custom-config';
import { authenticationInProgress } from '../../ducks/Auth.duck';
import {
  buildPackageInquirySteps,
  STEP_TRIP_DETAILS,
  STEP_YOUR_ACCOUNT,
} from './packageInquirySteps';
import { mergeInquiryDraft, readInquiryDraft, writeInquiryDraft } from './inquiryDraft';

export const InquiryWizardContext = createContext();

export const STEP_ACTIVITY = {
  label: 'Activity',
  path: 'activity',
};
export const STEP_PACKAGES = {
  label: 'Packages',
  path: 'packages',
};
export const STEP_SPECIES = {
  label: 'Species',
  path: 'species',
};
export const STEP_PARTY = {
  label: 'Guests & Dates',
  path: 'guests-dates',
};
export const STEP_REVIEW = {
  label: 'Review',
  path: 'review',
};
export const STEP_SIGNUP = {
  label: 'Account Creation',
  path: 'signup',
};

const ALL_STEPS = [
  STEP_ACTIVITY,
  STEP_PACKAGES,
  STEP_SPECIES,
  STEP_PARTY,
  STEP_REVIEW,
  STEP_SIGNUP,
];

const ALL_STEPS_FOR_AUTH_USER = [
  STEP_ACTIVITY,
  STEP_PACKAGES,
  STEP_SPECIES,
  STEP_PARTY,
  STEP_REVIEW,
];

// Location state seeds a package inquiry, including the listing path that
// pushes bookingDates: undefined. Fields the user sets afterward must win.
export const resolvePackageInquiryFormValues = (formValues, locationState) => {
  const stateSaysPackage = locationState?.entry === 'package' && locationState.selectedPackage;
  if (!stateSaysPackage) {
    return formValues;
  }

  return { ...locationState, ...formValues, entry: 'package' };
};

const getStepUrl = (params, stepPath) => {
  return createResourceLocatorString(
    'NewInquiryPage',
    routeConfiguration(),
    {
      ...params,
      step: stepPath,
    },
    {}
  );
};

const getStepsFromFormValues = (formValues, params, hasCurrentUser, isPackageInquiry = false) => {
  if (isPackageInquiry || formValues?.entry === 'package') {
    return buildPackageInquirySteps(hasCurrentUser, params.step).map(step => ({
      ...step,
      ...(step.path ? { url: getStepUrl(params, step.path) } : {}),
    }));
  }

  const hasCurrentUserOnFirstStep = hasCurrentUser && params.step === STEP_REVIEW.path;
  const hasPackage = formValues?.selectedPackage;
  const hasDiyPackage = formValues?.selectedPackage?.activity === HUNT_DIY_ACTIVITY;
  if (hasCurrentUserOnFirstStep) {
    return ALL_STEPS_FOR_AUTH_USER.filter(step => {
      if (step.label === STEP_SPECIES.label && hasPackage && !hasDiyPackage) {
        return false;
      }

      return true;
    }).map(step => ({
      ...step,
      url: getStepUrl(params, step.path),
    }));
  }
  return ALL_STEPS.filter(step => {
    if (step.label === STEP_SPECIES.label && hasPackage && !hasDiyPackage) {
      return false;
    }

    return true;
  }).map(step => ({
    ...step,
    url: getStepUrl(params, step.path),
  }));
};

const getStepIndex = (formValues, steps, stepPath) => {
  const currentStepIndex = steps.findIndex(step => step.path === stepPath);
  const stepLabel = steps[currentStepIndex]?.label;

  if (currentStepIndex < 0) {
    const tripDetailsIndex = steps.findIndex(step => step.id === STEP_TRIP_DETAILS.id);
    return tripDetailsIndex >= 0 ? tripDetailsIndex : 0;
  }

  if (stepLabel === STEP_TRIP_DETAILS.label) {
    return currentStepIndex;
  }

  if (stepLabel === STEP_YOUR_ACCOUNT.label) {
    const dates = formValues.bookingDates || {};
    const message = typeof formValues.message === 'string' ? formValues.message.trim() : '';
    if (
      formValues.selectedPackage &&
      formValues.partySize &&
      dates.startDate &&
      dates.endDate &&
      message
    ) {
      return currentStepIndex;
    }

    const tripDetailsIndex = steps.findIndex(step => step.id === STEP_TRIP_DETAILS.id);
    return tripDetailsIndex >= 0 ? tripDetailsIndex : 0;
  }

  // Validate the steps against formValues
  switch (stepLabel) {
    case STEP_ACTIVITY.label: {
      return currentStepIndex;
    }

    case STEP_PACKAGES.label: {
      if (formValues.activity) {
        return currentStepIndex;
      }

      return getStepIndex(formValues, steps, steps[currentStepIndex - 1]);
    }

    case STEP_SPECIES.label: {
      if (formValues.activity && formValues.selectedPackage) {
        return currentStepIndex;
      }

      return getStepIndex(formValues, steps, steps[currentStepIndex - 1]);
    }

    case STEP_PARTY.label: {
      if (formValues.activity && formValues.selectedPackage) {
        if (formValues.selectedPackage?.activity === HUNT_DIY_ACTIVITY && !formValues.species) {
          return getStepIndex(formValues, steps, steps[currentStepIndex - 1]);
        }

        return currentStepIndex;
      }

      return getStepIndex(formValues, steps, steps[currentStepIndex - 1]);
    }
    case STEP_SIGNUP.label: {
      if (
        formValues.activity &&
        formValues.selectedPackage &&
        formValues.partySize &&
        formValues.bookingDates
      ) {
        return currentStepIndex;
      }

      return getStepIndex(formValues, steps, steps[currentStepIndex - 1]);
    }

    case STEP_REVIEW.label: {
      if (
        formValues.activity &&
        formValues.selectedPackage &&
        formValues.partySize &&
        formValues.bookingDates
      ) {
        if (formValues.selectedPackage?.activity === HUNT_DIY_ACTIVITY && !formValues.species) {
          return currentStepIndex;
        }

        return currentStepIndex;
      }

      return getStepIndex(formValues, steps, steps[currentStepIndex - 1]);
    }

    default:
      return 0;
  }
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
  };
};

export const InquiryWizardProviderBase = ({ children, defaultValues, currentUser }) => {
  const { params, history, location } = defaultValues;
  const { state } = location;
  const hasCurrentUser = !!currentUser?.id;
  const stateSaysPackage = state?.entry === 'package';
  const packageStep =
    params.step === STEP_TRIP_DETAILS.path || params.step === STEP_YOUR_ACCOUNT.path;
  const shouldRestoreDraft = stateSaysPackage || (!state && packageStep);
  // The draft lives in localStorage, which the server can't read. On a hard load of a
  // package step (refresh, or "Return to your inquiry" after email verification) wait for
  // the client before validating the step; otherwise SSR redirects to the activity step.
  const waitForClientDraft = !state && packageStep;
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  // Listing inquiries adopt the package trip-details flow once a package is chosen.
  // Later step pushes do not resend location state, so the flag stays latched.
  const [latchedPackageInquiry, setLatchedPackageInquiry] = useState(() => {
    const draft = shouldRestoreDraft ? readInquiryDraft(params.id) : null;
    return stateSaysPackage || (!state && draft?.entry === 'package');
  });
  const isPackageInquiry = latchedPackageInquiry || stateSaysPackage;
  const [formValues, setFormValues] = useState(() => {
    const draft = shouldRestoreDraft ? readInquiryDraft(params.id) : null;
    return resolvePackageInquiryFormValues(
      mergeInquiryDraft(state || {}, draft),
      stateSaysPackage ? state : null
    );
  });
  const resolvedFormValues = useMemo(
    () => resolvePackageInquiryFormValues(formValues, stateSaysPackage ? state : null),
    [formValues, state, stateSaysPackage]
  );

  useEffect(() => {
    if (!stateSaysPackage) {
      return undefined;
    }

    setLatchedPackageInquiry(true);
    setFormValues(current => resolvePackageInquiryFormValues(current, state));
    return undefined;
  }, [state, stateSaysPackage]);

  useEffect(() => {
    if (!isPackageInquiry) {
      return undefined;
    }
    writeInquiryDraft(params.id, resolvedFormValues);
    return undefined;
  }, [isPackageInquiry, params.id, resolvedFormValues]);

  // Based on values build the steps
  const steps = useMemo(
    () => getStepsFromFormValues(resolvedFormValues, params, hasCurrentUser, isPackageInquiry),
    [resolvedFormValues, params, hasCurrentUser, isPackageInquiry]
  );
  const stepIndex = getStepIndex(resolvedFormValues, steps, params.step);

  const next = useCallback(
    values => {
      const possibleSteps = values
        ? getStepsFromFormValues(values, params, hasCurrentUser, isPackageInquiry)
        : steps;
      const url = getStepUrl(params, possibleSteps[stepIndex + 1].path);

      history.push(url);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps, params, hasCurrentUser, isPackageInquiry]
  );

  if (waitForClientDraft && !hydrated) {
    return null;
  }

  // Check if step is invalid, if so go back to the correct step
  const paramStepIndex = steps.findIndex(step => step.path === params.step);

  if (paramStepIndex !== stepIndex) {
    const fallbackPath = steps[stepIndex]?.path || steps.find(item => item.path)?.path;
    if (fallbackPath) {
      history.push(getStepUrl(params, fallbackPath));
    }

    return null;
  }

  const isFirst = stepIndex === 0;
  const isLast = stepIndex + 1 === steps.length;
  const step = steps[stepIndex];

  const hasNext = !isLast;
  const hasPrev = !isFirst;

  const value = {
    isFirst,
    isLast,

    hasNext,
    hasPrev,

    next,

    steps,
    step,
    stepIndex,
    setFormValues,
    formValues: resolvedFormValues,
    isPackageInquiry,
  };

  return <InquiryWizardContext.Provider value={value}>{children}</InquiryWizardContext.Provider>;
};
export const InquiryWizardProvider = withRouter(
  connect(mapStateToProps)(InquiryWizardProviderBase)
);
export const useInquiryWizard = () => useContext(InquiryWizardContext);
